import React, { useMemo } from 'react';
import { ConnectedUser } from '../../../stores/types/connection';
import { RESTRICTED_USER_STATES } from '../../../stores/reducers/session';
import { OpentokSessionValue } from '../../../connection/health';
import { Probe } from '../types';
import HealthPanel from '../health-panel';
import CopyValue from '../copy-value';

const RESTRICTED_LABELS: Record<number, string> = {
	[RESTRICTED_USER_STATES.CAMERA]: 'camera blocked',
	[RESTRICTED_USER_STATES.MICROPHONE]: 'mic blocked',
	[RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE]: 'cam + mic blocked',
	[RESTRICTED_USER_STATES.BLOCKED]: 'blocked',
	[RESTRICTED_USER_STATES.RECONNECTING]: 'reconnecting',
};

interface Props {
	probe: Probe<OpentokSessionValue> | undefined;
	users: ConnectedUser[];
}

/**
 * OpenTok session as sync provisioned it, plus the publishers that should exist
 * inside it. Publisher identity comes from the connected users each client
 * reported up through the socket - it is what the room expects to be publishing,
 * not a read of Vonage's own stream list.
 */
export default function OpentokPanel({ probe, users }: Props) {
	const publishers = useMemo(
		() => users.filter((user) => !!user.webrtcID),
		[users],
	);

	const screenshares = useMemo(
		() => users.filter((user) => !!user.screenShareWebrtcId),
		[users],
	);

	const feedCount = useMemo(
		() => users.reduce((total, user) => total + (user.feedWebRTCIds?.length ?? 0), 0),
		[users],
	);

	return (
		<HealthPanel title='OpenTok session' source='sync · opentok session entity' probe={probe}>
			<dl className='health-facts'>
				<dt>Session</dt>
				<dd><CopyValue value={probe?.data?.sessionId ?? ''} mono /></dd>
				<dt>Media region</dt>
				<dd>{probe?.data?.region ?? 'default'}</dd>
				<dt>Publishers</dt>
				<dd>
					{publishers.length}
					{!!screenshares.length && ` · ${screenshares.length} screenshare`}
					{!!feedCount && ` · ${feedCount} feed id${feedCount === 1 ? '' : 's'}`}
				</dd>
			</dl>

			<p className='health-subtle'>
				Identity and presence only. Bitrate, packet loss and frame rate live in each
				participant&apos;s browser and are not readable from here.
			</p>

			{!publishers.length ? (
				<p className='health-panel__note'>No client is reporting a publisher id.</p>
			) : (
				<table className='health-table'>
					<thead>
						<tr>
							<th>Publisher</th>
							<th>webrtcID</th>
							<th>State</th>
						</tr>
					</thead>
					<tbody>
						{publishers.map((user) => {
							const restricted = user.restrictedUserState
								? RESTRICTED_LABELS[user.restrictedUserState]
								: undefined;

							return (
								<tr key={String(user.webrtcID)}>
									<td>
										{user.first_name} {user.last_name}
										{user.isProducer && <span className='health-tag'>producer</span>}
									</td>
									<td className='health-mono'>{user.webrtcID}</td>
									<td>
										{!user.in_room && <span className='health-tag health-tag--warn'>not in room</span>}
										{user.watcher_only && <span className='health-tag'>watcher only</span>}
										{restricted && <span className='health-tag health-tag--warn'>{restricted}</span>}
										{user.in_room && !user.watcher_only && !restricted && 'publishing'}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			)}
		</HealthPanel>
	);
}
